import { useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TipTapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { X, Save, Upload, Trash2, Plus, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3, Link2, Quote, Undo2, Redo2, Palette, Layers, Move, RotateCcw, Crosshair } from 'lucide-react';
import PrintZoneEditor from './PrintZoneEditor';

const API = process.env.REACT_APP_BACKEND_URL;

function ToolbarBtn({ onClick, active, children, title }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${active ? 'bg-[#00E5FF]/20 text-[#00E5FF]' : 'text-slate-400 hover:text-white hover:bg-white/10'}`}>
      {children}
    </button>
  );
}

function ImageUploadSmall({ imagePath, onUpload, onClear, label }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/api/admin/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onUpload(data.path);
    } catch (err) { console.error('Upload error:', err); }
    finally { setUploading(false); }
  };
  return imagePath ? (
    <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#1a1a1a]">
      <img src={`${API}/api/files/${imagePath}`} alt={label} className="w-full h-24 object-contain" />
      <button type="button" onClick={onClear} className="absolute top-1 right-1 w-5 h-5 rounded bg-[#0A0A0A]/80 flex items-center justify-center text-slate-400 hover:text-rose-400">
        <X className="w-2.5 h-2.5" />
      </button>
    </div>
  ) : (
    <label className="flex items-center justify-center h-24 border border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#00E5FF]/30 transition-colors">
      <span className="text-[10px] text-slate-500">{uploading ? '...' : <><Upload className="w-3 h-3 inline mr-1" />Upload</>}</span>
      <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
    </label>
  );
}

/* ---- Views Editor ---- */
function ViewsEditor({ views, onChange, colorVariants, productFinishes = [] }) {
  const [editingZone, setEditingZone] = useState(null);
  const addView = () => {
    const id = `view-${Date.now()}`;
    onChange([...views, { id, name: '', image: '', print_zone: { x: 25, y: 25, width: 50, height: 50 }, print_price: 0, finish_zones: {} }]);
  };
  const removeView = (idx) => onChange(views.filter((_, i) => i !== idx));
  const updateView = (idx, field, value) => {
    const u = [...views];
    u[idx] = { ...u[idx], [field]: value };
    if (field === 'name' && !views[idx].id.startsWith('view-')) {
      // don't change existing ids
    } else if (field === 'name') {
      u[idx].id = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    }
    onChange(u);
  };
  const updatePrintZone = (idx, zone) => {
    const u = [...views];
    u[idx] = { ...u[idx], print_zone: zone };
    onChange(u);
  };

  // Build a map of color images for the editor
  const getProductImages = () => {
    const images = {};
    views.forEach(v => { if (v.image) images[v.id] = v.image; });
    colorVariants?.forEach(c => { if (c.images) Object.assign(images, c.images); });
    return images;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-slate-400 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Product Views</label>
        <button type="button" onClick={addView} data-testid="add-view-btn" className="text-[10px] font-medium text-[#00E5FF] hover:text-white transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Add View</button>
      </div>
      {views.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
          <p className="text-xs text-slate-500">No views yet. Add perspectives like "Front", "Back", "Left Sleeve", "Wrist".</p>
        </div>
      ) : (
        <div className="space-y-3">
          {views.map((v, i) => (
            <div key={i} className="border border-white/10 rounded-xl p-3 bg-white/[0.02]">
              <div className="flex items-center gap-2 mb-2">
                <input value={v.name} onChange={(e) => updateView(i, 'name', e.target.value)} placeholder="View name (e.g. Front, Left Sleeve)"
                  data-testid={`view-name-${i}`} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-slate-500">+£</span>
                  <input type="number" step="0.01" value={v.print_price || 0} onChange={(e) => updateView(i, 'print_price', parseFloat(e.target.value) || 0)}
                    data-testid={`view-print-price-${i}`} title="Extra charge for printing this view"
                    className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                </div>
                <span className="text-[10px] text-slate-600 font-mono">{v.id}</span>
                <button type="button" onClick={() => removeView(i)} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              {(v.print_price || 0) > 0 && (
                <p className="text-[10px] text-[#00E5FF]/60 mb-2 pl-1">Uploading a logo to this view adds +£{(v.print_price || 0).toFixed(2)}</p>
              )}
              {/* Default image for this view */}
              <div className="mb-2">
                <span className="text-[10px] text-slate-500 block mb-1">Default Image</span>
                <ImageUploadSmall imagePath={v.image} label={v.name} onUpload={(path) => updateView(i, 'image', path)} onClear={() => updateView(i, 'image', '')} />
              </div>
              {/* Print Zone - Toggle between compact view and visual editor */}
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] text-slate-500 flex items-center gap-1"><Crosshair className="w-3 h-3" /> Print Zone</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-mono text-[#00E5FF]">{Math.round(v.print_zone?.x||0)}%, {Math.round(v.print_zone?.y||0)}% — {Math.round(v.print_zone?.width||0)}x{Math.round(v.print_zone?.height||0)}%</span>
                    {productFinishes.length > 1 && Object.keys(v.finish_zones || {}).length > 0 && (
                      <span className="text-[9px] text-amber-400 font-mono">+{Object.keys(v.finish_zones).length} finish zone{Object.keys(v.finish_zones).length > 1 ? 's' : ''}</span>
                    )}
                    <button type="button" onClick={() => setEditingZone(editingZone === i ? null : i)}
                      data-testid={`toggle-zone-editor-${i}`}
                      className={`text-[9px] font-medium px-2 py-1 rounded-lg border transition-colors ${editingZone === i ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5' : 'border-white/10 text-slate-500 hover:text-white'}`}>
                      {editingZone === i ? 'Close Editor' : 'Visual Editor'}
                    </button>
                  </div>
                </div>
                {editingZone === i && (
                  <div className="mt-2">
                    <PrintZoneEditor
                      view={v}
                      productImages={getProductImages()}
                      productFinishes={productFinishes}
                      onSave={(updatedView) => {
                        const u = [...views];
                        u[i] = { ...u[i], print_zone: updatedView.print_zone, finish_zones: updatedView.finish_zones || u[i].finish_zones };
                        onChange(u);
                      }}
                    />
                  </div>
                )}
              </div>
              {/* Allowed Finishes — checkboxes per finish */}
              {productFinishes.length >= 1 && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <span className="text-[10px] text-slate-500 block mb-1.5">Allowed Finishes</span>
                  <div className="flex flex-wrap gap-2">
                    {productFinishes.map((f) => {
                      const allowed = !v.allowed_finishes || v.allowed_finishes.length === 0 || v.allowed_finishes.includes(f.label);
                      return (
                        <label key={f.label} className="inline-flex items-center gap-1.5 cursor-pointer" data-testid={`view-${i}-finish-${f.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          <input type="checkbox" checked={allowed}
                            onChange={(e) => {
                              const u = [...views];
                              // Build explicit list from current state
                              let current = v.allowed_finishes && v.allowed_finishes.length > 0
                                ? [...v.allowed_finishes]
                                : productFinishes.map(pf => pf.label);
                              if (e.target.checked) {
                                if (!current.includes(f.label)) current.push(f.label);
                              } else {
                                current = current.filter(l => l !== f.label);
                                // Don't allow unchecking ALL — at least one must remain
                                if (current.length === 0) return;
                              }
                              u[i] = { ...u[i], allowed_finishes: current };
                              onChange(u);
                            }}
                            className="w-3.5 h-3.5 rounded border-white/20 bg-white/5 text-[#00E5FF] accent-[#00E5FF]" />
                          <span className={`text-[10px] font-medium ${allowed ? 'text-slate-300' : 'text-slate-600'}`}>{f.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {v.allowed_finishes && v.allowed_finishes.length > 0 && v.allowed_finishes.length < productFinishes.length && (
                    <p className="text-[9px] text-amber-400/70 mt-1">
                      {productFinishes.filter(f => !v.allowed_finishes.includes(f.label)).map(f => f.label).join(', ')} disabled for {v.name || 'this view'}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Color Variants Editor (with per-view images) ---- */
function ColorVariantsEditor({ variants, onChange, views }) {
  const addVariant = () => {
    const images = {};
    views.forEach(v => { images[v.id] = ''; });
    onChange([...variants, { name: '', hex_code: '#000000', images }]);
  };
  const removeVariant = (idx) => onChange(variants.filter((_, i) => i !== idx));
  const updateField = (idx, field, value) => {
    const u = [...variants]; u[idx] = { ...u[idx], [field]: value }; onChange(u);
  };
  const updateImage = (idx, viewId, path) => {
    const u = [...variants];
    u[idx] = { ...u[idx], images: { ...u[idx].images, [viewId]: path } };
    onChange(u);
  };
  const handleImageUpload = async (idx, viewId, file) => {
    try {
      const token = localStorage.getItem('admin_token');
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/admin/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      updateImage(idx, viewId, data.path);
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-slate-400 flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Colour Variants</label>
        <button type="button" onClick={addVariant} data-testid="add-color-variant" className="text-[10px] font-medium text-[#00E5FF] hover:text-white transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Add Colour</button>
      </div>
      {variants.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-xl p-4 text-center"><p className="text-xs text-slate-500">No colour variants. Add colours with preview images for each view.</p></div>
      ) : (
        <div className="space-y-3">
          {variants.map((v, i) => (
            <div key={i} className="border border-white/10 rounded-xl p-3 bg-white/[0.02]">
              <div className="flex items-center gap-2 mb-3">
                <input type="color" value={v.hex_code} onChange={(e) => updateField(i, 'hex_code', e.target.value)}
                  data-testid={`color-hex-${i}`} className="w-8 h-8 rounded-lg border border-white/10 cursor-pointer bg-transparent p-0.5" />
                <input value={v.name} onChange={(e) => updateField(i, 'name', e.target.value)} placeholder="Colour name"
                  data-testid={`color-name-${i}`} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                <span className="text-[10px] text-slate-500 font-mono">{v.hex_code}</span>
                <button type="button" onClick={() => removeVariant(i)} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"><Trash2 className="w-3 h-3" /></button>
              </div>
              {views.length > 0 && (
                <div className={`grid gap-2 ${views.length > 2 ? 'grid-cols-3' : views.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {views.map(view => (
                    <div key={view.id}>
                      <span className="text-[10px] text-slate-500 block mb-1">{view.name || view.id}</span>
                      {v.images?.[view.id] ? (
                        <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#1a1a1a]">
                          <img src={`${API}/api/files/${v.images[view.id]}`} alt={`${v.name} ${view.name}`} className="w-full h-20 object-contain" />
                          <button type="button" onClick={() => updateImage(i, view.id, '')}
                            className="absolute top-1 right-1 w-5 h-5 rounded bg-[#0A0A0A]/80 flex items-center justify-center text-slate-400 hover:text-rose-400"><X className="w-2.5 h-2.5" /></button>
                        </div>
                      ) : (
                        <label className="flex items-center justify-center h-20 border border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#00E5FF]/30 transition-colors">
                          <span className="text-[10px] text-slate-500"><Upload className="w-3 h-3 inline mr-1" />Upload</span>
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleImageUpload(i, view.id, e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Sub-Options Editor ---- */
function SubOptionsEditor({ subOptions, onChange }) {
  const addGroup = () => onChange([...subOptions, { name: '', options: [{ label: '', price_modifier: 0 }] }]);
  const removeGroup = (idx) => onChange(subOptions.filter((_, i) => i !== idx));
  const updateGroupName = (idx, name) => { const u = [...subOptions]; u[idx] = { ...u[idx], name }; onChange(u); };
  const addOption = (gi) => { const u = [...subOptions]; u[gi] = { ...u[gi], options: [...u[gi].options, { label: '', price_modifier: 0 }] }; onChange(u); };
  const removeOption = (gi, oi) => { const u = [...subOptions]; u[gi] = { ...u[gi], options: u[gi].options.filter((_, i) => i !== oi) }; onChange(u); };
  const updateOption = (gi, oi, field, value) => {
    const u = [...subOptions]; const opts = [...u[gi].options];
    opts[oi] = { ...opts[oi], [field]: field === 'price_modifier' ? parseFloat(value) || 0 : value };
    u[gi] = { ...u[gi], options: opts }; onChange(u);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium text-slate-400">Sub-Options & Pricing</label>
        <button type="button" onClick={addGroup} data-testid="add-option-group" className="text-[10px] font-medium text-[#00E5FF] hover:text-white transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Add Group</button>
      </div>
      {subOptions.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-xl p-4 text-center"><p className="text-xs text-slate-500">No sub-options yet.</p></div>
      ) : (
        <div className="space-y-3">
          {subOptions.map((group, gi) => (
            <div key={gi} className="border border-white/10 rounded-xl p-3 bg-white/[0.02]">
              <div className="flex items-center gap-2 mb-2">
                <input value={group.name} onChange={(e) => updateGroupName(gi, e.target.value)} placeholder="Group name (e.g. Size)" data-testid={`option-group-name-${gi}`}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                <button type="button" onClick={() => removeGroup(gi)} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"><Trash2 className="w-3 h-3" /></button>
              </div>
              <div className="space-y-1.5">
                {group.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input value={opt.label} onChange={(e) => updateOption(gi, oi, 'label', e.target.value)} placeholder="Option label" data-testid={`option-label-${gi}-${oi}`}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-slate-500">+£</span>
                      <input type="number" step="0.01" value={opt.price_modifier} onChange={(e) => updateOption(gi, oi, 'price_modifier', e.target.value)} data-testid={`option-price-${gi}-${oi}`}
                        className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                    </div>
                    <button type="button" onClick={() => removeOption(gi, oi)} className="w-6 h-6 rounded-md flex items-center justify-center text-slate-600 hover:text-rose-400 transition-colors"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => addOption(gi)} className="mt-2 text-[10px] text-slate-500 hover:text-[#00E5FF] transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Add option</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ MAIN EDITOR ============ */
export default function ProductEditor({ product, onSave, onCancel }) {
  const [title, setTitle] = useState(product?.title || '');
  const [slug, setSlug] = useState(product?.slug || '');
  const [basePrice, setBasePrice] = useState(product?.base_price || 0);
  const [defaultPrice, setDefaultPrice] = useState(product?.default_price ?? product?.base_price ?? 0);
  const [category, setCategory] = useState(product?.category || '');
  const [status, setStatus] = useState(product?.status || 'active');
  const [views, setViews] = useState(product?.views || []);
  const [subOptions, setSubOptions] = useState(product?.sub_options || []);
  const [colorVariants, setColorVariants] = useState(product?.color_variants || []);
  const [finishes, setFinishes] = useState(product?.finishes || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      TipTapLink.configure({ openOnClick: false, HTMLAttributes: { class: 'text-[#00B4D8] underline' } }),
      Underline,
    ],
    content: product?.description || '',
    editorProps: { attributes: { class: 'prose prose-invert prose-sm max-w-none min-h-[120px] focus:outline-none px-4 py-3' } },
  });

  const addLink = () => { const url = window.prompt('Enter URL:'); if (url && editor) editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run(); };

  const handleSave = useCallback(async () => {
    const description = editor ? editor.getHTML() : '';
    if (!title.trim()) { setError('Title is required'); return; }
    if (basePrice < 0) { setError('Price must be positive'); return; }
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const body = { title, slug, description, base_price: basePrice, default_price: defaultPrice, category, status, views, sub_options: subOptions, color_variants: colorVariants, finishes };
      const url = product?.id ? `${API}/api/admin/products/${product.id}` : `${API}/api/admin/products`;
      const method = product?.id ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      onSave(data);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }, [title, slug, basePrice, defaultPrice, category, status, views, subOptions, colorVariants, finishes, editor, product, onSave]);

  const categories = ['apparel', 'drinkware', 'bags', 'stationery', 'bundles'];

  return (
    <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Outfit'] text-lg font-bold text-white">{product?.id ? 'Edit Product' : 'New Product'}</h3>
        <button onClick={onCancel} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-5">
        {/* Basic Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Title</label>
            <input data-testid="product-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="Product name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Slug</label>
            <input data-testid="product-slug" value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" placeholder="auto-generated" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Base Price (£)</label>
            <input data-testid="product-price" type="number" step="0.01" value={basePrice} onChange={(e) => setBasePrice(parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Default Price (£)</label>
            <input data-testid="product-default-price" type="number" step="0.01" value={defaultPrice} onChange={(e) => setDefaultPrice(parseFloat(e.target.value) || 0)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Category</label>
            <select data-testid="product-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 appearance-none">
              <option value="">Select category</option>
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Status</label>
            <select data-testid="product-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF]/50 appearance-none">
              <option value="active">Active</option>
              <option value="draft">Draft</option>
            </select>
          </div>
        </div>
        {/* Description */}
        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Description</label>
          {editor && (
            <div className="border border-white/10 rounded-xl overflow-hidden bg-white/5">
              <div className="flex items-center flex-wrap gap-0.5 px-2 py-1.5 border-b border-white/10 bg-white/[0.02]">
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')}><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')}><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')}><UnderlineIcon className="w-3.5 h-3.5" /></ToolbarBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })}><Heading2 className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })}><Heading3 className="w-3.5 h-3.5" /></ToolbarBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')}><List className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')}><ListOrdered className="w-3.5 h-3.5" /></ToolbarBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')}><Quote className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={addLink} active={editor.isActive('link')}><Link2 className="w-3.5 h-3.5" /></ToolbarBtn>
                <div className="w-px h-4 bg-white/10 mx-1" />
                <ToolbarBtn onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-3.5 h-3.5" /></ToolbarBtn>
                <ToolbarBtn onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-3.5 h-3.5" /></ToolbarBtn>
              </div>
              <EditorContent editor={editor} />
            </div>
          )}
        </div>
        {/* Finishes (product-level) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-slate-400">Finishes <span className="text-slate-600">(applies to all sides)</span></label>
            <button type="button" onClick={() => setFinishes(prev => [...prev, { label: '', price_modifier: 0 }])}
              className="text-[10px] font-medium text-[#00E5FF] hover:text-white transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Add Finish</button>
          </div>
          {finishes.length > 0 ? (
            <div className="space-y-1.5 bg-white/[0.02] border border-white/10 rounded-xl p-3">
              {finishes.map((f, fi) => (
                <div key={fi} className="flex items-center gap-2">
                  <input value={f.label} onChange={(e) => { const u = [...finishes]; u[fi] = { ...u[fi], label: e.target.value }; setFinishes(u); }}
                    placeholder="e.g. Print, Embroidery" className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-500">+£</span>
                    <input type="number" step="0.01" value={f.price_modifier} onChange={(e) => { const u = [...finishes]; u[fi] = { ...u[fi], price_modifier: parseFloat(e.target.value) || 0 }; setFinishes(u); }}
                      className="w-16 bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-white text-xs focus:outline-none focus:border-[#00E5FF]/50" />
                  </div>
                  <button type="button" onClick={() => setFinishes(prev => prev.filter((_, i) => i !== fi))}
                    className="w-6 h-6 rounded-md flex items-center justify-center text-slate-600 hover:text-rose-400 transition-colors"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <p className="text-[9px] text-slate-600 mt-1">Each finish can have a custom print zone per view (set below in Views)</p>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500">No finishes configured. Add finishes like Print, Embroidery with different pricing.</p>
            </div>
          )}
        </div>
        {/* Views */}
        <ViewsEditor views={views} onChange={setViews} colorVariants={colorVariants} productFinishes={finishes} />
        {/* Sub-Options */}
        <SubOptionsEditor subOptions={subOptions} onChange={setSubOptions} />
        {/* Color Variants */}
        <ColorVariantsEditor variants={colorVariants} onChange={setColorVariants} views={views} />
        {/* Error & Actions */}
        {error && <p className="text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">{error}</p>}
        <div className="flex gap-3 pt-2">
          <button data-testid="product-save" onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : (product?.id ? 'Update Product' : 'Create Product')}
          </button>
          <button onClick={onCancel} className="px-6 py-3 rounded-xl text-sm font-medium text-slate-400 border border-white/10 hover:text-white hover:border-white/20 transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}
